import { getPaymentMethods, linePayRequest, linePayConfirm, linePayCancel, jkoPayEntry, jkoPayInquiry } from '@/services/api.service';

// 定義支付方式類型常數
const PAYMENT_METHOD_TYPE = Object.freeze({
    LINE_PAY: 1,
    CASH: 2,
    JKO_PAY: 5,
});

// 街口設定類型：OD = OnlinePay，同一 type 5 下還有其他街口設定，必須再比對
const JKOPAY_CONFIG_TYPE = 'OD';

// 街口 Inquiry 的最終狀態（非最終狀態須繼續輪詢，不可判定失敗）
export const JKOPAY_FINAL_STATUS = Object.freeze([
    'SUCCEEDED',
    'FAILED',
    'MANUAL_REVIEW',
    'REFUNDED',
    'PARTIALLY_REFUNDED',
]);

// 定義訂單來源位元位置常數
const ORDER_SOURCE_BIT_POSITION = Object.freeze({
    ONLINE: 1,
    // 可以新增其他位置...
});

export default {
    namespaced: true,
    state: {
        page: 0,
        // 文件要求：page_size 預設只有 10，支付方式較多的商店會撈不到街口
        page_size: 50,
        paymentMethodsList: [],
        paymentUrl: '',
        // 街口冪等鍵：跨分頁返回時可能取不到 route query，作為 fallback
        // （payment 模組已列入 vuex-persistedstate paths）
        jkoPayAttemptUuid: ''
    },
    getters: {
        linePayPaymentMethod: (state, getters) => {
            return state.paymentMethodsList.find(paymentMethod =>
                paymentMethod.payment_method_type === PAYMENT_METHOD_TYPE.LINE_PAY &&
                getters.isOnlineOrderEnabled(paymentMethod)
            ) || null;
        },
        // 街口 OnlinePay：type 5 + enabled + config.type === 'OD'
        // 不可寫死 store_payment_methods_config_id，一律由此查得
        jkoPayPaymentMethod: (state, getters) => {
            return state.paymentMethodsList.find(paymentMethod => {
                if (paymentMethod.payment_method_type !== PAYMENT_METHOD_TYPE.JKO_PAY) return false;
                if (!getters.isOnlineOrderEnabled(paymentMethod)) return false;
                return getters.parsePaymentMethodConfig(paymentMethod).type === JKOPAY_CONFIG_TYPE;
            }) || null;
        },
        // Entry 要帶的 store_payment_methods_config_id
        // 相容兩種欄位命名（既有 LINE Pay 用 store_payment_methods_config_id，文件範例用 id）
        jkoPayPaymentMethodId: (state, getters) => {
            const method = getters.jkoPayPaymentMethod;
            if (!method) return null;
            return method.store_payment_methods_config_id ?? method.id ?? null;
        },
        // 付款選項顯示文字，後端未設定時退回「街口支付」
        jkoPayDisplayName: (state, getters) => {
            const method = getters.jkoPayPaymentMethod;
            return method?.store_payment_method_name || '街口支付';
        },
        parsePaymentMethodConfig: () => (paymentMethod) => {
            const config = paymentMethod?.payment_method_config;
            if (!config) return {};
            if (typeof config === 'object') return config;
            try {
                return JSON.parse(config) || {};
            } catch (e) {
                console.error('payment_method_config 解析失敗', e);
                return {};
            }
        },
        cashPaymentMethod: (state, getters) => {
            const cashPaymentMethod = state.paymentMethodsList.find(paymentMethod => paymentMethod.payment_method_type === PAYMENT_METHOD_TYPE.CASH);
            if (!cashPaymentMethod) {
                return null
            }
            if (getters.isOnlineOrderEnabled(cashPaymentMethod)) {
                return cashPaymentMethod;
            }
            return null;
        },
        isCashPaymentEnabled: (state, getters) => {
            const hasCashConfig = state.paymentMethodsList.some(paymentMethod => paymentMethod.payment_method_type === PAYMENT_METHOD_TYPE.CASH);
            // 相容舊店家設定：若後端未回傳現金設定，沿用既有行為視為可用
            if (!hasCashConfig) {
                return true;
            }
            return !!getters.cashPaymentMethod;
        },
        isOnlineOrderEnabled: (state, getters) => (paymentMethod) => {
            const enabled = paymentMethod.enabled === 1;
            if (!enabled) {
                return false;
            }

            return getters.isFunctionEnabled(paymentMethod.order_source_switch, ORDER_SOURCE_BIT_POSITION.ONLINE);
        },
        isFunctionEnabled: () => (decimal, bitPosition) => {
            // 使用 bit 位數判斷十進位值在二進位值的 value
            if (decimal === undefined) return false
            return (decimal & (1 << bitPosition)) !== 0;
        },
    },
    mutations: {
        setPaymentMethods(state, payload) {
            state.paymentMethodsList = payload;
        },
        setPaymentUrl(state, payload) {
            state.paymentUrl = payload;
        },
        setJkoPayAttemptUuid(state, payload) {
            state.jkoPayAttemptUuid = payload || '';
        }
    },
    actions: {
        async getPaymentMethods({ commit, state }) {
            // page_size 未明確傳入時後端預設只有 10，支付方式多的商店會漏掉街口
            const paymentMethodsResponse = (await getPaymentMethods({
                page: state.page,
                // payment 模組會持久化，避免舊版 localStorage 的 10 蓋掉新版下限
                page_size: Math.max(Number(state.page_size) || 0, 50),
            })).data.data
            console.log('paymentMethodsResponse', paymentMethodsResponse);
            if (paymentMethodsResponse.msg !== 'Success') {
                throw (paymentMethodsResponse.err_msg)
            }
            commit('setPaymentMethods', paymentMethodsResponse.payment_methods_config_info_list);
        },
        async linePayRequest({ commit }, data) {
            const linePayRequestResponse = (await linePayRequest(data)).data.data;
            console.log('linePayRequestResponse', linePayRequestResponse);
            if (linePayRequestResponse.msg !== 'Success') {
                throw (linePayRequestResponse.err_msg)
            }
            commit('setPaymentUrl', linePayRequestResponse.paymentUrl);
        },
        async linePayConfirm(context, data) {
            const linePayConfirmResponse = (await linePayConfirm(data)).data.data;
            console.log('linePayConfirmResponse', linePayConfirmResponse);
            if (linePayConfirmResponse.msg !== 'Success') {
                console.error('linePayConfirmResponse', linePayConfirmResponse.err_msg);
            }
        },
        async linePayCancel(data) {
            const linePayCancelResponse = await linePayCancel(data);
            console.log('linePayCancelResponse', linePayCancelResponse);
            if (linePayCancelResponse.msg !== 'Success') {
                throw (linePayCancelResponse.err_msg)
            }
        },
        // 街口 Entry：建立付款，成功後取得 paymentUrl（欄位比照 LINE Pay，可共用跳轉邏輯）
        async jkoPayEntry({ commit }, data) {
            const res = (await jkoPayEntry(data)).data.data;
            console.log('jkoPayEntryResponse', res);
            if (res.error_code !== 0 || !res.paymentUrl) {
                const error = new Error(res.err_msg || '街口付款建立失敗');
                error.jkopayErrorCode = res.jkopay_error_code;
                error.errorCode = res.error_code;
                throw error;
            }
            commit('setPaymentUrl', res.paymentUrl);
            return res;
        },
        // 街口 Inquiry：查詢最終狀態，回傳 status 字串
        async jkoPayInquiry(context, data) {
            const res = (await jkoPayInquiry(data)).data.data;
            console.log('jkoPayInquiryResponse', res);
            if (res.error_code !== 0) {
                const error = new Error(res.err_msg || '街口付款查詢失敗');
                error.jkopayErrorCode = res.jkopay_error_code;
                error.errorCode = res.error_code;
                throw error;
            }
            return res.info?.status || '';
        }
    }
}
